/** /onboarding-invites (+ /onboarding-invites/:id for the detail drawer) — the dashboard
 *  self-signup invite links (plan 2026-10-17-dashboard-self-signup-invite-links §15, D9, D14).
 *
 *  A QuerySearch grid over Onboarding_Invite (`?q=`; small table, no mandatory window), with
 *  presets, Refresh / auto-refresh and server-side sort. "New invite" creates one link or a batch
 *  and shows the links once in the result dialog (copy, QR, Print QR sheet). Row actions follow
 *  the status (`inviteActionRules.ts`); the link is fetched from the audited `link` endpoint only when
 *  an action needs it, never listed or cached.
 *
 *  Not tenant-scoped: an invite has no tenant until it is used (the `tenant` namespace finds the
 *  used ones). No batch operations: the API offers none. Views need
 *  `console_onboarding_invites_view`; every action needs `console_onboarding_invites_manage`.
 */
import AddIcon from '@mui/icons-material/Add';
import { Alert, Box, Button, CircularProgress } from '@mui/material';
import {
  DataGridPro,
  gridColumnFieldsSelector,
  useGridApiRef,
  type GridPaginationModel,
  type GridSlots,
  type GridToolbarProps,
} from '@mui/x-data-grid-pro';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { ConsoleApiError, isStatementTimeout } from '@/api/client';
import * as easyApi from '@/api/easyOnboarding';
import * as invitesApi from '@/api/onboardingInvites';
import type { InviteCreateResponse, InviteListItem } from '@/api/onboardingInvites';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import { GridControlsToolbar } from '@/components/DataGrid/GridControlsToolbar';
import { SearchTimeoutAlert } from '@/components/DataGrid/SearchTimeoutAlert';
import { gridRefreshToolbar, useGridAutoRefresh } from '@/components/DataGrid/useGridAutoRefresh';
import { PageHeader } from '@/components/PageHeader';
import { PermissionGate } from '@/components/PermissionGate';
import {
  QuerySearchInput,
  equalityOperator,
  parse,
  serialize,
  toWireAst,
  type FilterNode,
} from '@/components/QuerySearch';
import { useSnackbar } from '@/components/Snackbar';
import { useCreateIntent } from '@/hooks/useCreateIntent';
import { useGridPresets, type PresetFiltersAdapter } from '@/hooks/useGridPresets';
import { usePermissions } from '@/hooks/usePermissions';
import { PAGE_FILL_SX } from '@/layout/pageFill';

import { useInviteActionRunner } from './InviteActions';
import type { InviteActionPerms } from './inviteActionRules';
import { DEFAULT_HIDDEN, buildColumns } from './inviteColumns';
import { InviteCreatedDialog } from './InviteCreatedDialog';
import { InviteDetailDrawer } from './InviteDetailDrawer';
import { InviteFormDialog, type RestechOption } from './InviteFormDialog';
import { buildInviteNamespaces } from './inviteNamespaces';

const DEFAULT_PAGE_SIZE = 50;
const MANAGE = 'console_onboarding_invites_manage';

/** Presets store the filter state as the raw `?q=` DSL text (it round-trips through
 *  parse/serialize), like the API-credentials grid. */
const PRESET_FILTERS: PresetFiltersAdapter = {
  urlParams: ['q'],
  capture: (get) => {
    const q = get('q');
    return q ? { q } : undefined;
  },
  toUrlParams: (filters) => {
    const q = (filters as { q?: unknown } | null)?.q;
    return typeof q === 'string' && q ? { q } : ({} as Record<string, string>);
  },
};

export function OnboardingInvitesPage() {
  const snackbar = useSnackbar();
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: drawerId } = useParams<{ id?: string }>();
  const apiRef = useGridApiRef();
  const { hasPerm } = usePermissions();
  const perms = useMemo<InviteActionPerms>(
    () => ({ canManage: hasPerm(MANAGE), canOpenTenant: hasPerm('console_top_view') }),
    [hasPerm],
  );

  // The booking systems the self-signup can offer — the easy-onboarding registry. Its endpoint
  // needs the easy-onboarding permissions; without them the form takes typed codes instead.
  const canReadRegistry = hasPerm('console_top_manage') && hasPerm('console_preflight_manage');
  const registry = useQuery({
    queryKey: easyApi.easyStatusKey,
    queryFn: ({ signal }) => easyApi.getStatus(signal),
    enabled: canReadRegistry,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const restechOptions = useMemo<RestechOption[] | null>(
    () => registry.data?.restechs.map((r) => ({ code: r.code, available: r.available })) ?? null,
    [registry.data],
  );
  const namespaces = useMemo(
    () => buildInviteNamespaces({ restechs: restechOptions?.map((r) => r.code) }),
    [restechOptions],
  );

  const [createOpen, setCreateOpen] = useState(false);
  const [created, setCreated] = useState<InviteCreateResponse | null>(null);
  // ⌘K "Create new invite link" (`?new`).
  useCreateIntent(useCallback(() => setCreateOpen(true), []));

  const runner = useInviteActionRunner({ restechOptions });

  const presets = useGridPresets({
    grid: 'onboarding_invites',
    defaultOrder: useMemo(
      () =>
        buildColumns({
          onQuickFilter: () => undefined,
          detailPath: (id) => `/onboarding-invites/${id}`,
          perms,
          onRun: () => undefined,
        }).map((c) => c.field),
      [perms],
    ),
    defaultHidden: DEFAULT_HIDDEN,
    defaultOrdering: '-created_ts',
    filters: PRESET_FILTERS,
  });

  const q = presets.getParam('q') ?? '';
  const ordering = presets.ordering;

  const { tree, diagnostics } = useMemo(() => {
    const r = parse(q, namespaces);
    return { tree: r.nodes, diagnostics: r.diagnostics };
  }, [q, namespaces]);

  const lastDiagKey = useRef('');
  useEffect(() => {
    if (diagnostics.length === 0) return;
    const key = `${q}::${diagnostics.length}`;
    if (key === lastDiagKey.current) return;
    lastDiagKey.current = key;
    snackbar.show(`Some of your search couldn't be interpreted: ${diagnostics[0]!.message}`, 'warning');
  }, [diagnostics, q, snackbar]);

  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: DEFAULT_PAGE_SIZE });
  const [extendedTimeout, setExtendedTimeout] = useState(false);
  useEffect(() => {
    setPagination((p) => ({ ...p, page: 0 }));
    setExtendedTimeout(false);
  }, [q]);

  const { beforeUrlWrite } = presets;
  const setQuery = useCallback(
    (nodes: FilterNode[]) => {
      const next = new URLSearchParams(sp);
      beforeUrlWrite(next);
      const str = serialize(nodes);
      if (str) next.set('q', str);
      else next.delete('q');
      setSp(next, { replace: true });
    },
    [sp, setSp, beforeUrlWrite],
  );

  const onQuickFilter = useCallback(
    (namespace: string, value: string) => {
      const cfg = namespaces[namespace];
      const operator = cfg ? equalityOperator(cfg.operators) : null;
      if (!operator) return;
      setQuery([...tree, { kind: 'cond', namespace, operator, value }]);
    },
    [namespaces, tree, setQuery],
  );

  // Drawer routes keep the grid's query string, so closing the drawer restores the search.
  const detailPath = useCallback((id: number) => `/onboarding-invites/${id}${location.search}`, [location.search]);

  const autoRefresh = useGridAutoRefresh({ grid: 'onboarding_invites' });
  const query = useQuery({
    queryKey: [...invitesApi.invitesKeys.list(), pagination.page, pagination.pageSize, ordering, q, extendedTimeout],
    queryFn: () =>
      invitesApi.list({
        page: pagination.page + 1,
        page_size: pagination.pageSize,
        ordering,
        filters: toWireAst(tree),
        ...(extendedTimeout ? { timeout: 'extended' as const } : {}),
      }),
    placeholderData: keepPreviousData,
    ...autoRefresh.queryOptions(extendedTimeout),
    enabled: !presets.blocking,
  });

  const rows = useMemo(() => query.data?.results ?? [], [query.data?.results]);
  // Small table: the backend runs a plain count.
  const total = query.data && query.data.count >= 0 ? query.data.count : rows.length;

  const columns = useMemo(
    () => orderColumns(buildColumns({ onQuickFilter, detailPath, perms, onRun: runner.run }), presets.orderedFields),
    [onQuickFilter, detailPath, perms, runner.run, presets.orderedFields],
  );

  const apiError =
    query.error instanceof ConsoleApiError && query.error.status === 400 && !isStatementTimeout(query.error)
      ? query.error
      : null;
  const backendMessage = (apiError?.body as { filters?: string } | undefined)?.filters ?? apiError?.message ?? null;
  const shownOnce = useRef(false);
  useEffect(() => {
    if (backendMessage && !shownOnce.current) {
      shownOnce.current = true;
      snackbar.show(`Search rejected: ${backendMessage}`, 'error');
    }
    if (!backendMessage) shownOnce.current = false;
  }, [backendMessage, snackbar]);

  const hasSearch = q.trim().length > 0;

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="onboarding-invites"
        title="Onboarding invites"
        subtitle="Invite links for the dashboard self-signup — one per prospect, or a batch for a QR sheet. A link claims itself to one browser and is dead once the tenant and its admin exist."
        actions={
          <PermissionGate perm={MANAGE}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
              New invite
            </Button>
          </PermissionGate>
        }
      />

      <Box sx={{ mb: 2 }}>
        <QuerySearchInput
          namespaces={namespaces}
          value={tree}
          onChange={setQuery}
          placeholder="Search invites…  e.g. status=claimed & restech=fareharbor & created=2026-10-17"
        />
      </Box>

      {isStatementTimeout(query.error) && (
        <SearchTimeoutAlert
          extendedAttempted={extendedTimeout}
          onRetryExtended={!extendedTimeout ? () => setExtendedTimeout(true) : undefined}
          retrying={query.isFetching}
        />
      )}
      {backendMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {backendMessage}
        </Alert>
      )}
      {query.isError && !apiError && !isStatementTimeout(query.error) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Couldn’t load the invites.
        </Alert>
      )}

      {presets.blocking ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <DataGridPro<InviteListItem>
          apiRef={apiRef}
          rows={rows}
          rowCount={total}
          getRowId={(r) => r.id}
          columns={columns}
          loading={query.isLoading || query.isFetching}
          sx={{ flex: 1, minHeight: 360 }}
          onRowClick={(p) => navigate(detailPath(p.row.id))}
          pagination
          paginationMode="server"
          sortingMode="server"
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          sortModel={presets.sortModel}
          onSortModelChange={presets.onSortModelChange}
          columnVisibilityModel={presets.columnVisibilityModel}
          onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
          onColumnOrderChange={() => presets.onColumnOrderChange(gridColumnFieldsSelector(apiRef))}
          pageSizeOptions={[25, 50, 100]}
          disableRowSelectionOnClick
          disableColumnFilter
          slots={{
            noRowsOverlay: () => (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                {hasSearch ? 'No invites match this search.' : 'No invite links yet — create one to let a prospect onboard themselves.'}
              </Box>
            ),
            toolbar: GridControlsToolbar as GridSlots['toolbar'],
          }}
          slotProps={{
            loadingOverlay: { variant: 'linear-progress', noRowsVariant: 'skeleton' },
            toolbar: {
              refresh: gridRefreshToolbar(query, autoRefresh, { paused: extendedTimeout }),
              hideFilterButton: true,
              exportFileName: 'onboarding-invites',
              presets: presets.toolbar,
            } as GridToolbarProps,
          }}
        />
      )}

      {drawerId && /^\d+$/.test(drawerId) && (
        <InviteDetailDrawer
          inviteId={Number(drawerId)}
          open
          perms={perms}
          onRun={runner.run}
          onClose={() => navigate(`/onboarding-invites${location.search}`)}
        />
      )}

      <InviteFormDialog
        open={createOpen}
        restechOptions={restechOptions}
        onClose={() => setCreateOpen(false)}
        onCreated={setCreated}
      />
      <InviteCreatedDialog result={created} onClose={() => setCreated(null)} />
      {runner.elements}
    </Box>
  );
}
